import { splitLocalePath } from '../i18n/locales';

/**
 * Whether the person reading this plant page arrived on it from somebody else
 * (SPEC-019 D-7).
 *
 * The tempting signal is a parameter on the shared address — `?via=share` — and
 * it is wrong twice: it makes the link uglier in the one place a link is read as
 * text, and this product deliberately shares no address that can distinguish two
 * people's shares (CON-003 means there is nowhere to count them anyway).
 *
 * The app already knows. `pwa.visits` counts app STARTS, not navigations
 * (SPEC-016), so a visit counter at its first tick on a route that is a plant is
 * exactly the shape of "someone opened a link a friend sent". It is also right
 * for the path that carries no link at all — a card posted to a story, an
 * address typed off the wordmark at the foot of it.
 *
 * Pure, so the rule is testable without Web Storage or a router. `visits` is the
 * count AFTER this start has been counted, which is what `countVisit` returns.
 */

/**
 * A second start is somebody who came back, and somebody who came back has been
 * welcomed already.
 */
const FIRST_START = 1;

/** `/hu-hu/plants/lavender` — one plant, and not the shelf it came off. */
const PLANT_ROUTE = /^\/plants\/[^/]+$/u;

/**
 * Whether an address is one plant.
 *
 * Asked of the address the app STARTED on, never of the one it is on now. The
 * two differ the moment anybody navigates, and the difference is the whole
 * question: reaching a plant from the landing screen is not arriving on one.
 */
export function isPlantRoutePath(pathname) {
    return PLANT_ROUTE.test(splitLocalePath(pathname).routePath);
}

export function isSharedArrival({ visits, routeIsPlant } = {}) {
    return routeIsPlant === true && Number(visits) === FIRST_START;
}

/**
 * The language to offer, or nothing.
 *
 * Offered rather than obeyed: the sharer chose the language the link carries,
 * and a product that silently overrules that choice throws away the one thing
 * the shared page was for. So this answers a LANGUAGE to put in a link, and only
 * when the browser is asking for one this product speaks and the page is not
 * already in it.
 *
 * `browserLanguages` arrives as the browser wrote it — `en-GB`, `de-AT`, `hu` —
 * because `readBrowserLanguages` hands back `navigator.languages` untouched. The
 * primary subtag is what this product chooses along (`domain/i18n/languages.js`
 * is a list of three languages, not of regions), so the tags are cut down here,
 * the same first line `negotiateLocale.js` opens with.
 */
export function offeredLanguage({ pageLanguage, browserLanguages = [], supported = [] } = {}) {
    const wanted = browserLanguages.map((tag) => String(tag).toLowerCase().split('-')[0]);

    // Not "their first choice differs" but "the page speaks none of their
    // languages". A reader whose browser accepts Hungarian AND English, on a
    // Hungarian page, is already reading a language they asked for — offering
    // them English there is the app second-guessing both them and the sharer.
    if (wanted.includes(pageLanguage)) {
        return null;
    }

    return wanted.find((language) => supported.includes(language)) ?? null;
}
